<template>
  <div class="send-exams-container">
    <InitialView
      v-if="status === 'initial'"
      @main-action="chooseFile()"
      @send-files="sendFiles()"
      @secondary-action="openContinueWithoutExams()"
      @remove="(item) => removeFile(item)"
      :files="fileList"
    />
    <LoadingView v-if="status === 'uploading'" :showLabel="hasFileToSendToOCR" />
    <input
      type="file"
      name="file-chooser"
      id="file-chooser"
      ref="input"
      multiple
      @change="handleFileChange"
    />
  </div>
</template>

<script>
import { InitialView, LoadingView } from './partials';
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex';
import { dataLayersMixin } from '@/utils/mixins';
import { removeQueryParamsFromUrl } from '@/utils/helpers/removeQueryParamsFromUrl';

export default {
  mixins: [dataLayersMixin],
  components: {
    InitialView,
    LoadingView,
  },
  created() {
    this.checkAdmissionIdsOnMedicalOrders();
  },

  mounted() {
    this.fileList = this.getUploadedMedicalOrders;
  },

  data() {
    return {
      status: 'initial',
      fileList: [],
      showContinueWithoutAdvice: false,
      idPaperless: '',
      limitFiles: 10,
    };
  },

  computed: {
    ...mapState({
      currentAdmission: ({ currentAdmission }) => currentAdmission.data,
      payment: ({ payment }) => payment.data,
      admissionId: ({ currentAdmission }) => currentAdmission.data.id,
      healthInsuranceVariables: ({ healthInsurance }) =>
        healthInsurance.data.healthInsuranceVariables,
    }),
    ...mapGetters({
      getInsurancePlan: 'getInsurancePlan',
      isOcrEnableInUnit: 'isOcrEnableInUnit',
      getUploadedMedicalOrders: 'getUploadedMedicalOrders',
      hasMedicalOrdersSentToAnalyze: 'hasMedicalOrdersSentToAnalyze',
      getBrandUnitIdVariables: 'getBrandUnitIdVariables',
    }),
    hasFileToSendToOCR() {
      return !!this.getUploadedMedicalOrders.filter((item) => item.data)?.length;
    },
  },

  methods: {
    ...mapActions([
      'uploadFilesOnPaperless',
      'analyzeMedicalOrdersOnOcr',
      'getQuestionnaire',
      'checkAdmissionIdsOnMedicalOrders',
    ]),
    ...mapMutations([
      'SET_UPLOADED_MEDICAL_ORDERS',
      'SET_CLEAR_MEDICALORDERS',
      'SET_SEND_TO_ANALYZE',
    ]),
    chooseFile() {
      this.$refs.input.click();
    },
    async sendFiles() {
      this.status = 'uploading';

      const beforeUnloadListener = (event) => {
        event.preventDefault();
        return (event.returnValue = 'Tem certeza de que deseja sair da página?');
      };
      addEventListener('beforeunload', beforeUnloadListener, { capture: true });

      try {
        const currentFiles = this.getUploadedMedicalOrders.map((item) =>
          removeQueryParamsFromUrl(item.data?.documentUrl),
        );

        if (
          this.isOcrEnableInUnit &&
          this.hasFileToSendToOCR &&
          !this.hasMedicalOrdersSentToAnalyze
        ) {
          this.analyzeMedicalOrdersOnOcr({
            images: currentFiles,
            unitId: this.getBrandUnitIdVariables.unitId,
          });
        }

        this.SET_SEND_TO_ANALYZE(true);

        this.mostCommonPagesAnalytics({
          eventType: 'funnel_step',
          currentStep: 'Pedido médico enviado com sucesso',
        });
      } catch (err) {
        console.log('Error to send file to Send file > ', err);
      } finally {
        removeEventListener('beforeunload', beforeUnloadListener, { capture: true });
      }

      const promise1 = new Promise((resolve, reject) => {
        setTimeout(() => {
          resolve(true);
        }, 5000);
      });

      const promise2 = this.getQuestionnaire();

      Promise.all([promise1, promise2]).finally((_) => {
        this.$router.push({ name: 'questions', params: { answer: 'Enviar pedido médico' } });
        this.status = 'uploaded';
      });
    },

    async openContinueWithoutExams() {
      await this.$router.push({ name: 'questions', params: { answer: 'Não tenho pedido médico' } });
    },
    removeFile(item) {
      this.fileList = this.fileList.filter((file) => file.data.documentUrl !== item);

      this.SET_UPLOADED_MEDICAL_ORDERS(this.fileList);
    },
    async handleFileChange({ target: { files = [] } }) {
      const paymentType =
        this.currentAdmission.payments &&
        this.currentAdmission.payments.length &&
        this.currentAdmission.payments[0].details.type;

      try {
        if (this.fileList.length < this.limitFiles) {
          for await (const file of files) {
            const fileObject = {
              file,
              status: 'UPLOADING',
              data: {
                status: 'UPLOADING',
                name: file.name,
              },
            };
            this.fileList = [...this.fileList, fileObject];
            this.SET_UPLOADED_MEDICAL_ORDERS(this.fileList);
            let uploadFile;
            try {
              uploadFile = await this.uploadFilesOnPaperless({ file });
            } catch (err) {
              console.error('Error to upload file to paperless! > ', err);
            }
            const findFile = this.fileList.find((item) => item === fileObject);
            if (uploadFile) {
              findFile.data = {
                ...findFile.data,
                ...uploadFile,
                status: 'UPLOADED',
                admissionId: this.admissionId,
              };
            }
            findFile.data.status = 'UPLOADED';
          }

          this.SET_UPLOADED_MEDICAL_ORDERS(this.fileList);

          this.mostCommonPagesAnalytics({
            eventType: 'funnel_step',
            currentStep: 'Pedido médico adicionado',
          });
        }
      } catch (error) {
        this.mostCommonPagesAnalytics({
          eventType: 'funnel_step',
          currentStep: 'Erro ao adicionar Pedido médico',
        });
      } finally {
        this.$refs.input.value = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.send-exams-container {
  flex: 1;
  padding: 0;
  display: flex;
  flex-direction: column;
  height: auto;
  justify-content: space-between;
  align-items: center;
  align-content: center;
  position: relative;
  overflow: hidden;

  input {
    display: none;
  }
}

.advice-before-leave {
  flex: 1;
  justify-content: space-between;
  display: flex;
  flex-direction: column;
  font-family: 'Dasa sans', sans-serif;

  &__info {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  h4 {
    font-size: 16px;
    line-height: 28px;
    color: #323232;
    font-weight: var(--font-weight-bold);
    margin-bottom: 8px;
  }

  p {
    font-size: 16px;
    line-height: 20.8px;
    color: #161616b8;
    padding: 0 17px;
    margin-bottom: 24px;
  }

  button {
    padding: 12px 0;
    text-align: center;
    background: #652cdf;
    border: 1px solid #652cdf;
    border-radius: 4px;
    color: white;
    font-size: 16px;
    line-height: 24px;
    font-weight: var(--font-weight-bold);
  }
}
</style>
